import React, { useEffect } from "react";
import { useTranslate, useOne, useUpdate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Tooltip,
  Row,
  Col,
  Radio,
  Select,
  Button,
  Spin,
  Divider,
} from "antd";
import { InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";

const { Title } = Typography;

type ProductEditModalProps = {
  open: boolean;
  productId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const ProductEditModal: React.FC<ProductEditModalProps> = ({
  open,
  productId,
  onCancel,
  onSuccess,
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm();

  const { data, isLoading } = useOne({
    resource: "products",
    id: productId ?? "",
    queryOptions: {
      enabled: open && !!productId,
    },
  });

  const record = data?.data;

  const { mutate: updateProduct, isLoading: isUpdating } = useUpdate();

  const { selectProps } = useSelect({
    resource: "forms",
    optionValue: "id",
    optionLabel: "id",
    defaultValue: record?.form?.id,
  });

  useEffect(() => {
    if (record && open) {
      const initialValues: any = {
        name: record.name,
        code: record.code,
        type: record.type,
        price: record.price,
        description: record.description,
        group: record.group,
        tags: record.tags ?? [],
        formId: record.form?.id,
      };

      form.setFieldsValue(initialValues);
    }

    if (!open) {
      form.resetFields();
    }
  }, [record, open, form]);

  const handleFinish = (values: any) => {
    if (!productId) return;

    updateProduct(
      {
        resource: "products",
        id: productId,
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <Modal
      title={translate("products.titles.edit")}
      open={open}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{translate("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating}
            onClick={() => form.submit()}
          >
            {translate("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="!mt-4"
        >
          <Space direction="vertical" size="large" className="w-full">
            <Title level={5}>{translate("products.fields.basicInfo")}</Title>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.name")}
                      <Tooltip
                        title={translate("products.fields.tooltips.name")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["name"]}
                  rules={[
                    {
                      required: true,
                      message: translate("fields.errors.required"),
                    },
                  ]}
                >
                  <Input
                    placeholder={translate("products.fields.placeholders.name")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.code")}
                      <Tooltip
                        title={translate("products.fields.tooltips.code")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["code"]}
                  rules={[
                    {
                      required: true,
                      message: translate("fields.errors.required"),
                    },
                  ]}
                >
                  <Input
                    placeholder={translate("products.fields.placeholders.code")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.type")}
                      <Tooltip
                        title={translate("products.fields.tooltips.type")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["type"]}
                  rules={[
                    {
                      required: true,
                      message: translate("fields.errors.required"),
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value="base">
                      {translate("products.fields.typeValues.base")}
                    </Radio>
                    <Radio value="option">
                      {translate("products.fields.typeValues.option")}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.group")}
                      <Tooltip
                        title={translate("products.fields.tooltips.group")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["group"]}
                  rules={[
                    {
                      max: 100,
                      message: translate("fields.errors.maxLength", {
                        max: 100,
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={translate(
                      "products.fields.placeholders.group",
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.tags")}
                      <Tooltip
                        title={translate("products.fields.tooltips.tags")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["tags"]}
                >
                  <Select
                    mode="tags"
                    placeholder={translate(
                      "products.fields.placeholders.tags",
                    )}
                    tokenSeparators={[","]}
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.description")}
                      <Tooltip
                        title={translate(
                          "products.fields.tooltips.description",
                        )}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["description"]}
                  rules={[
                    {
                      max: 500,
                      message: translate("fields.errors.maxLength", {
                        max: 500,
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder={translate(
                      "products.fields.placeholders.description",
                    )}
                    rows={4}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider className="!my-0" />

            <Title level={5}>
              {translate("products.fields.pricingDetails")}
            </Title>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={
                    <Space>
                      {translate("products.fields.price")}
                      <Tooltip
                        title={translate("products.fields.tooltips.price")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name={["price"]}
                >
                  <InputNumber
                    min={0}
                    stringMode
                    precision={0}
                    placeholder={translate(
                      "products.fields.placeholders.price",
                    )}
                    className="w-full"
                    addonBefore="Crédit"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={translate("products.fields.form")}
              name={"formId"}
              hidden
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Select {...selectProps} />
            </Form.Item>
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};
